import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  Container,
  IconButton,
  Grid,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Assessment,
  ExpandMore,
  ExpandLess,
  ArrowUpward,
  ArrowDownward,
  FilterList,
  Download,
} from '@mui/icons-material';
import api from '../services/api';
import MinMaxCalc from './MinMaxCalc';
import DeactivateItem from './DeactivateItem';
import MergeTwoItems from './MergeTwoItems';

interface ItemReportData {
  [key: string]: any;
  store_id: number;
  store_name: string;
  manufacturer_id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  inventory_quantity: number;
  min_quantity?: number;
  max_quantity?: number;
}

interface ColumnFilter {
  value: string;
  operator?: string;
}

const Reports: React.FC = () => {
  // Item Reports States
  const [itemReportsLoading, setItemReportsLoading] = useState(false);
  const [itemReportsError, setItemReportsError] = useState<string | null>(null);
  const [itemReportsData, setItemReportsData] = useState<ItemReportData[]>([]);
  const [selectedSections, setSelectedSections] = useState<string[]>([
    'sales_activity',
    'purchase_activity',
    'aggregated_purchase',
    'aggregated_sales',
    'profit_margin'
  ]);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    sales_activity: false,
    purchase_activity: false,
    aggregated_purchase: false,
    top_distributor: false,
    aggregated_sales: false,
    profit_margin: false,
    order_requests: false,
    other_stores: false
  });
  const [columnFilters, setColumnFilters] = useState<{[key: string]: ColumnFilter}>({});
  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc' | 'desc'} | null>(null);
  const [itemReportsDownloading, setItemReportsDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'minmax' | 'deactivate' | 'merge' | null>(null);
  const [showBelowMinStock, setShowBelowMinStock] = useState(false);

  const toggleSection = (section: string) => {
    setSelectedSections((prev: string[]) => {
      const isRemoving = prev.includes(section);
      const newSections = isRemoving
        ? prev.filter((s: string) => s !== section)
        : [...prev, section];
      
      // Reset "Below Minimum Stock" filter if sales_activity is deselected
      if (section === 'sales_activity' && isRemoving && showBelowMinStock) {
        setShowBelowMinStock(false);
      }
      
      return newSections;
    });
  };

  const toggleSectionExpanded = (section: string) => {
    setExpandedSections((prev: {[key: string]: boolean}) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExecuteItemReports = async () => {
    if (selectedSections.length === 0) {
      setItemReportsError('Please select at least one section');
      return;
    }

    setItemReportsLoading(true);
    setItemReportsError(null);
    setPage(0); // Reset to first page on new report

    try {
      const savedStore = localStorage.getItem('selectedStore');
      let storeId = null;
      
      if (savedStore) {
        try {
          const store = JSON.parse(savedStore);
          storeId = store.id;
        } catch (error) {
          console.error('Error parsing saved store:', error);
        }
      }

      if (!storeId) {
        setItemReportsError('Please select a store first from the top menu');
        return;
      }

      const response = await api.get(`/utilities/item-reports`, {
        params: { 
          store_id: storeId,
          sections: selectedSections.join(',')
        }
      });
      
      if (response.data.success) {
        setItemReportsData(response.data.data);
        if (response.data.data.length === 0) {
          setItemReportsError('No data found for the selected store');
        }
      } else {
        setItemReportsError('Failed to generate report');
      }
    } catch (error: any) {
      console.error('Error generating item reports:', error);
      setItemReportsError(error.response?.data?.message || 'Failed to generate item reports. Please try again.');
    } finally {
      setItemReportsLoading(false);
    }
  };

  const handleFilterChange = (column: string, value: string, operator?: string) => {
    const newOperator = operator !== undefined ? operator : (columnFilters[column]?.operator || '=');
    
    setColumnFilters((prev: {[key: string]: ColumnFilter}) => ({
      ...prev,
      [column]: { 
        value, 
        operator: newOperator
      }
    }));
    setPage(0); // Reset to first page when filtering
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setPage(0); // Reset to first page when clearing filters
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const getRowKey = (row: ItemReportData) => {
    return `${row.store_id}_${row.manufacturer_id}`;
  };

  const handleSelectRow = (row: ItemReportData) => {
    const key = getRowKey(row);
    setSelectedRows((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allKeys = getFilteredAndSortedData()
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(getRowKey);
      setSelectedRows(new Set(allKeys));
    } else {
      setSelectedRows(new Set());
    }
  };

  const getSelectedItems = (): ItemReportData[] => {
    return getFilteredAndSortedData().filter(row => selectedRows.has(getRowKey(row)));
  };

  const handleOpenActionDialog = () => {
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedAction(null);
  };

  const handleActionSelect = (action: 'minmax' | 'deactivate' | 'merge') => {
    setSelectedAction(action);
    setActionDialogOpen(false);
  };

  const handleActionComplete = () => {
    setSelectedAction(null);
    setSelectedRows(new Set());
  };

  const handleSort = (column: string) => {
    setSortConfig((prev: {key: string; direction: 'asc' | 'desc'} | null) => {
      if (prev?.key === column) {
        return { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: column, direction: 'asc' };
    });
    setPage(0); // Reset to first page when sorting
  };

  const getFilteredAndSortedData = (): ItemReportData[] => {
    let filtered = [...itemReportsData];

    // Apply filters
    Object.keys(columnFilters).forEach(column => {
      const filter = columnFilters[column];
      if (filter.value && filter.value.trim() !== '') {
        filtered = filtered.filter(row => {
          let cellValue = row[column];
          
          // Check if this column should be treated as numeric
          const isNumericColumn = getColumnType(column) === 'number';
          
          if (isNumericColumn) {
            // Convert string numbers to actual numbers
            const numericCellValue = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
            const numFilter = parseFloat(filter.value);
            
            // If conversion failed, skip this row
            if (isNaN(numericCellValue) || isNaN(numFilter)) {
              return false;
            }
            
            // Apply operator - default to '=' if not specified
            const operator = filter.operator || '=';
            
            switch (operator) {
              case '>': return numericCellValue > numFilter;
              case '<': return numericCellValue < numFilter;
              case '>=': return numericCellValue >= numFilter;
              case '<=': return numericCellValue <= numFilter;
              case '=': return numericCellValue === numFilter;
              default: return numericCellValue === numFilter;
            }
          }
          
          // For text/date columns (starts with)
          if (cellValue === null || cellValue === undefined) return false;
          const filterValue = filter.value.toLowerCase();
          return String(cellValue).toLowerCase().startsWith(filterValue);
        });
      }
    });

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        // Check if this is a numeric column
        const isNumericColumn = getColumnType(sortConfig.key) === 'number';
        
        if (isNumericColumn) {
          // Convert to numbers for numeric comparison
          const aNum = typeof aVal === 'number' ? aVal : parseFloat(String(aVal));
          const bNum = typeof bVal === 'number' ? bVal : parseFloat(String(bVal));
          
          // Handle NaN values
          if (isNaN(aNum)) return 1;
          if (isNaN(bNum)) return -1;
          
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // For text columns, use string comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Apply "Below Minimum Stock" filter
    if (showBelowMinStock) {
      filtered = filtered.filter(row => {
        // Skip rows that don't have min_quantity (required for this filter)
        if (row.min_quantity === null || row.min_quantity === undefined) {
          return false;
        }
        
        // Convert to numbers to handle string values from backend
        const inventory = typeof row.inventory_quantity === 'number' 
          ? row.inventory_quantity 
          : parseFloat(String(row.inventory_quantity || 0));
        const minQty = typeof row.min_quantity === 'number' 
          ? row.min_quantity 
          : parseFloat(String(row.min_quantity || 0));
        
        // Only include rows where inventory is less than min quantity
        return inventory < minQty;
      });
    }

    return filtered;
  };

  const handleDownloadItemReportsCsv = async () => {
    setItemReportsDownloading(true);
    
    try {
      const data = getFilteredAndSortedData();
      const csvContent = convertItemReportsToCsv(data);
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `item-reports-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      setItemReportsError('Failed to download CSV. Please try again.');
    } finally {
      setItemReportsDownloading(false);
    }
  };

  const convertItemReportsToCsv = (data: ItemReportData[]): string => {
    if (data.length === 0) return '';
    
    // Use the same column ordering and formatting as the table
    const headers = getDisplayColumns(data);
    const csvRows = [headers.map(col => formatColumnName(col)).join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return `"${JSON.stringify(value)}"`;
        if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
        return String(value);
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  };

  const getColumnType = (column: string): 'number' | 'text' => {
    // Define all numeric columns from the backend query
    // These columns should use numeric comparison operators (>, <, >=, <=, =)
    const numericColumns = [
      // IDs
      'store_id', 
      'manufacturer_id', 
      // Note: purchase_top_distributor_id is treated as text for display purposes
      // The name field (purchase_top_distributor_name) will be shown instead
      
      // Inventory
      'inventory_quantity',
      'min_quantity',
      'max_quantity',
      
      // Sales activity metrics
      'sales_bills_30d', 
      'sales_bills_60d',
      'sales_total_sold_60d', 
      'sales_total_sold_90d', 
      'sales_total_sold_180d',
      
      // Purchase activity metrics
      'purchase_bills_30d', 
      'purchase_bills_60d',
      'purchase_total_qty_90d',
      
      // Purchase pricing (aggregated averages)
      'purchase_avg_mrp', 
      'purchase_avg_ptr', 
      'purchase_avg_base_price', 
      'purchase_avg_discount_price',
      'purchase_avg_gst', 
      'purchase_avg_invoice_price',
      
      // Sales pricing (aggregated averages)
      'sales_avg_selling_price', 
      'sales_avg_landing_price', 
      'sales_avg_gst',
      
      // Profit metrics
      'sales_profit_margin_pct',
      'sales_profit_margin_pct_7d',
      'sales_profit_margin_pct_30d',
      
      // Order request metrics
      'order_request_bills_30d', 
      'order_request_items_30d'
    ];
    return numericColumns.includes(column) ? 'number' : 'text';
  };

  const formatColumnName = (column: string): string => {
    // Special formatting for better readability
    if (column === 'purchase_top_distributor_name') {
      return 'Top Distributor';
    }
    if (column === 'purchase_top_distributor_id') {
      return 'Top Distributor ID';
    }
    if (column === 'sales_profit_margin_pct_7d') {
      return 'Sales Profit Margin Pct (Last 1 Week)';
    }
    if (column === 'sales_profit_margin_pct_30d') {
      return 'Sales Profit Margin Pct (Last 1 Month)';
    }
    if (column === 'min_quantity') {
      return 'Min Quantity';
    }
    if (column === 'max_quantity') {
      return 'Max Quantity';
    }
    // Replace underscores with spaces and capitalize each word
    return column
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Get columns to display, prioritizing name over ID when both exist
  const getDisplayColumns = (data?: ItemReportData[]): string[] => {
    const sourceData = data || itemReportsData;
    if (sourceData.length === 0) return [];
    const allColumns = Object.keys(sourceData[0]);
    
    // Define preferred order for core columns
    const coreColumns = [
      'store_id', 'store_name', 
      'manufacturer_id', 'manufacturer_name', 
      'medicine_name', 'pack_size',
      'inventory_quantity', 'min_quantity', 'max_quantity'
    ];
    
    // Separate core columns and other columns
    const core = coreColumns.filter(col => allColumns.includes(col));
    const others = allColumns.filter(col => !coreColumns.includes(col));
    
    // Sort others to put name columns before id columns
    others.sort((a, b) => {
      if (a.includes('_name') && b.includes('_id')) return -1;
      if (a.includes('_id') && b.includes('_name')) return 1;
      return 0;
    });
    
    return [...core, ...others];
  };

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          Reports
        </Typography>
        
        <Typography variant="body1" color="textSecondary">
          Generate and analyze comprehensive reports with configurable data sections
        </Typography>
      </Box>

      {/* Item Reports View */}
          {/* Section Selector */}
          <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          Select Report Sections
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Choose which data sections to include in your report
        </Typography>
        
        <Grid container spacing={2}>
          {/* Sales Activity */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSections.includes('sales_activity')}
                    onChange={() => toggleSection('sales_activity')}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight="medium">Sales Activity</Typography>
                    <IconButton size="small" onClick={(e: React.MouseEvent) => { e.preventDefault(); toggleSectionExpanded('sales_activity'); }}>
                      {expandedSections.sales_activity ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </Box>
                }
              />
              <Collapse in={expandedSections.sales_activity}>
                <Box sx={{ pl: 4, mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    • Sales bills (30d)<br/>
                    • Total sold (60d, 90d, 180d)<br/>
                    • Last sale date
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          {/* Purchase Activity */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('purchase_activity')}
                        onChange={() => toggleSection('purchase_activity')}
                  />
                }
                label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">Purchase Activity</Typography>
                        <IconButton size="small" onClick={(e: React.MouseEvent) => { e.preventDefault(); toggleSectionExpanded('purchase_activity'); }}>
                          {expandedSections.purchase_activity ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                }
              />
              <Collapse in={expandedSections.purchase_activity}>
                    <Box sx={{ pl: 4, mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        • Purchase bills (30d)<br/>
                        • Total quantity (90d)<br/>
                        • Last purchase date
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          {/* Aggregated Purchase */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('aggregated_purchase')}
                        onChange={() => toggleSection('aggregated_purchase')}
                  />
                }
                label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">Aggregated Purchase</Typography>
                        <IconButton size="small" onClick={(e: React.MouseEvent) => { e.preventDefault(); toggleSectionExpanded('aggregated_purchase'); }}>
                          {expandedSections.aggregated_purchase ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                }
              />
              <Collapse in={expandedSections.aggregated_purchase}>
                    <Box sx={{ pl: 4, mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        • Avg MRP, PTR, Base Price<br/>
                        • Avg Discount, GST<br/>
                        • Avg Invoice Price
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          {/* Top Distributor */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('top_distributor')}
                        onChange={() => toggleSection('top_distributor')}
                  />
                }
                label={<Typography variant="body2" fontWeight="medium">Top Distributor</Typography>}
                  />
            </Paper>
          </Grid>

          {/* Aggregated Sales */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('aggregated_sales')}
                        onChange={() => toggleSection('aggregated_sales')}
                  />
                }
                label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">Aggregated Sales</Typography>
                        <IconButton size="small" onClick={(e: React.MouseEvent) => { e.preventDefault(); toggleSectionExpanded('aggregated_sales'); }}>
                          {expandedSections.aggregated_sales ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                }
              />
              <Collapse in={expandedSections.aggregated_sales}>
                    <Box sx={{ pl: 4, mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        • Avg Selling Price<br/>
                        • Avg Landing Price<br/>
                        • Avg GST
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          {/* Profit Margin */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('profit_margin')}
                        onChange={() => toggleSection('profit_margin')}
                  />
                }
                label={<Typography variant="body2" fontWeight="medium">Profit Margin %</Typography>}
                  />
            </Paper>
          </Grid>

          {/* Order Requests */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('order_requests')}
                        onChange={() => toggleSection('order_requests')}
                  />
                }
                label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">Order Requests</Typography>
                        <IconButton size="small" onClick={(e: React.MouseEvent) => { e.preventDefault(); toggleSectionExpanded('order_requests'); }}>
                          {expandedSections.order_requests ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                }
              />
              <Collapse in={expandedSections.order_requests}>
                    <Box sx={{ pl: 4, mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        • Order bills (30d)<br/>
                        • Order items (30d)<br/>
                        • Last order date
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          {/* Other Stores */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                        checked={selectedSections.includes('other_stores')}
                        onChange={() => toggleSection('other_stores')}
                  />
                }
                label={<Typography variant="body2" fontWeight="medium">Other Stores Sales</Typography>}
                  />
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleExecuteItemReports}
            disabled={itemReportsLoading || selectedSections.length === 0}
            startIcon={itemReportsLoading ? <CircularProgress size={16} /> : <Assessment />}
            color="info"
            size="large"
          >
            {itemReportsLoading ? 'Generating...' : 'Execute Report'}
          </Button>
          
          {itemReportsData.length > 0 && (
            <Button 
              variant="outlined" 
              onClick={handleDownloadItemReportsCsv}
              disabled={itemReportsDownloading}
              startIcon={itemReportsDownloading ? <CircularProgress size={16} /> : <Download />}
              color="success"
              size="large"
            >
              {itemReportsDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          )}
        </Box>
      </Paper>

      {itemReportsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {itemReportsError}
        </Alert>
      )}

      {/* Results Table */}
      {itemReportsData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Report Results ({getFilteredAndSortedData().length} items)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                size="small" 
                onClick={handleOpenActionDialog}
                disabled={selectedRows.size === 0}
              >
                Actions ({selectedRows.size} selected)
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={clearAllFilters}
                disabled={Object.keys(columnFilters).length === 0}
              >
                Clear All Filters
              </Button>
            </Box>
          </Box>
          
          {selectedSections.includes('sales_activity') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showBelowMinStock}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setShowBelowMinStock(e.target.checked);
                    setPage(0); // Reset to first page
                  }}
                />
              }
              label="Show Items Below Minimum Stock"
              sx={{ mb: 2 }}
            />
          )}
          
          <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                    <Checkbox
                      indeterminate={selectedRows.size > 0 && selectedRows.size < getFilteredAndSortedData().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                      checked={getFilteredAndSortedData().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 && selectedRows.size === getFilteredAndSortedData().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {itemReportsData[0] && getDisplayColumns().map((column) => (
                    <TableCell key={column} sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
                      <Box>
                        <Box 
                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                          onClick={() => handleSort(column)}
                        >
                          <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                            {formatColumnName(column)}
                          </Typography>
                          {sortConfig?.key === column && (
                            sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                        {getColumnType(column) === 'number' ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <FormControl size="small" sx={{ minWidth: 70 }}>
                              <Select
                                value={columnFilters[column]?.operator || '='}
                                onChange={(e: any) => {
                                  const newOperator = String(e.target.value);
                                  const currentValue = columnFilters[column]?.value || '';
                                  handleFilterChange(column, currentValue, newOperator);
                                }}
                                displayEmpty
                              >
                                <MenuItem value="=">=</MenuItem>
                                <MenuItem value=">">&gt;</MenuItem>
                                <MenuItem value="<">&lt;</MenuItem>
                                <MenuItem value=">=">&gt;=</MenuItem>
                                <MenuItem value="<=">&lt;=</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              placeholder="Filter"
                              value={columnFilters[column]?.value || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(column, e.target.value, columnFilters[column]?.operator || '=')}
                              sx={{ minWidth: 80 }}
                            />
                          </Box>
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Filter (starts with)"
                            value={columnFilters[column]?.value || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(column, e.target.value)}
                          />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredAndSortedData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={`row-${page * rowsPerPage + index}`} hover selected={selectedRows.has(getRowKey(row))}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.has(getRowKey(row))}
                          onChange={() => handleSelectRow(row)}
                        />
                      </TableCell>
                      {getDisplayColumns().map((column) => (
                        <TableCell key={column}>
                          {row[column] !== null && row[column] !== undefined 
                            ? (typeof row[column] === 'object' 
                                ? JSON.stringify(row[column]) 
                                : String(row[column])) 
                            : '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination Controls */}
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 250, 500]}
            component="div"
            count={getFilteredAndSortedData().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </Paper>
      )}

      {/* Action Selection Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleCloseActionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Select Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose an action to perform on {selectedRows.size} selected items:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleActionSelect('minmax')}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Min-Max Calculator
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Calculate and set minimum/maximum inventory levels
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleActionSelect('deactivate')}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Deactivate Items
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bulk update item deactivation status
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleActionSelect('merge')}
              disabled={selectedRows.size !== 2}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Merge Two Items
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Merge sales/purchase data from one item to another
                </Typography>
                {selectedRows.size !== 2 && (
                  <Typography variant="caption" color="error" display="block">
                    Select exactly 2 items to merge
                  </Typography>
                )}
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Min-Max Calculator Component */}
      {selectedAction === 'minmax' && (
        <MinMaxCalc
          open={true}
          onClose={() => setSelectedAction(null)}
          selectedItems={getSelectedItems()}
          onComplete={handleActionComplete}
        />
      )}

      {/* Deactivate Item Component */}
      {selectedAction === 'deactivate' && (
        <DeactivateItem
          open={true}
          onClose={() => setSelectedAction(null)}
          selectedItems={getSelectedItems()}
          onComplete={handleActionComplete}
        />
      )}

      {/* Merge Two Items Component */}
      {selectedAction === 'merge' && (
        <MergeTwoItems
          open={true}
          onClose={() => setSelectedAction(null)}
          selectedItems={getSelectedItems()}
          onComplete={handleActionComplete}
        />
      )}
    </Container>
  );
};

export default Reports;

